/**
 * Setup-Logik für den Travel-Stresstest.
 * Generiert Pathfinding-Requests, verteilt auf Worker, liefert Routen.
 */
import type { WorldInfo } from '../config/WorldConfig';
import type { TravelConfig } from '../config/TravelConfig';
import type { FleetConfig } from '../config/FleetConfig';
import type { RenderPipeline } from '../renderer/RenderPipeline';
import type { PathfindingRequest, PathfindingResponse } from '../workers/pathfinding.types';
import { TravelLineRenderer } from '../objects/TravelLineRenderer';
import { FleetState } from './FleetState';
import { TravelAnimator } from './TravelAnimator';
import { TravelMode } from './TravelTypes';
import type { TravelRoute } from './TravelTypes';
import { buildPathfindingRequest } from './TravelRequestBuilder';
import { buildTravelRoute } from './TravelRouteBuilder';
import { hexDistance } from '../math/HexMath';
import { SHIP_TYPE_DEFS, SHIP_TYPE_COUNT } from '../config/ShipTypes';

export { MAX_SHIPS_PER_FLEET } from './TravelStressTestConst';

export const MAX_ROUTE_COUNT = 100;
const WORKER_COUNT = 4;
const MIN_ROUTE_DIST = 20;

export interface StressRoute {
  line: TravelLineRenderer;
  animator: TravelAnimator;
  fleetState: FleetState;
  route: TravelRoute;
}

const _stressComposition: number[] = [];
for (let i = 0; i < SHIP_TYPE_COUNT; i++) _stressComposition.push(SHIP_TYPE_DEFS[i].displayMax);

function pathfindOne(worker: Worker, req: PathfindingRequest): Promise<PathfindingResponse> {
  return new Promise((resolve) => {
    worker.onmessage = (e: MessageEvent<PathfindingResponse>) => {
      worker.onmessage = null;
      resolve(e.data);
    };
    worker.postMessage(req);
  });
}

function createStressRoute(
  resp: PathfindingResponse, cfg: TravelConfig, hexRadius: number,
  gl: WebGL2RenderingContext, fleetConfig: FleetConfig, pipeline: RenderPipeline,
  showLines: boolean,
): StressRoute | null {
  if (resp.type !== 'path-found' || resp.route.length < 2) return null;
  const route = buildTravelRoute(resp.route, TravelMode.Normal, cfg);
  const line = new TravelLineRenderer(gl, cfg);
  line.buildFromHexes(
    resp.route, hexRadius, cfg.flightHeight,
    route.segmentKinds, cfg.routeColors.normal, cfg.routeColors.warp,
  );
  const fleetState = new FleetState(fleetConfig);
  fleetState.setComposition(_stressComposition);
  fleetState.active = true;
  const animator = new TravelAnimator(hexRadius);
  animator.start(route, null);
  line.setSuppressed(!showLines);
  pipeline.addRenderable(line);
  return { line, animator, fleetState, route };
}

export async function generateStressRoutes(
  count: number, gl: WebGL2RenderingContext, worldInfo: WorldInfo,
  travelConfig: TravelConfig, fleetConfig: FleetConfig,
  pipeline: RenderPipeline, sunKeys: number[], showLines: boolean,
  seedRef: { seed: number },
): Promise<StressRoute[]> {
  const n = Math.min(Math.max(1, count), MAX_ROUTE_COUNT);
  const w = worldInfo.worldWidthHexes;
  const h = worldInfo.worldHeightHexes;
  const rng = () => ((seedRef.seed = (seedRef.seed * 1103515245 + 12345) % 2147483648) / 2147483648);
  const hexRadius = worldInfo.config.hexRadius;
  const margin = 2;
  const rMin = margin, rMax = Math.max(rMin, h - 1 - margin);
  const colMin = margin, colMax = Math.max(colMin, w - 1 - margin);
  const colSpan = Math.max(1, colMax - colMin + 1);
  const rSpan = Math.max(1, rMax - rMin + 1);

  const requests: PathfindingRequest[] = [];
  for (let i = 0; i < n; i++) {
    let sq = 0, sr = 0, eq = 0, er = 0;
    for (let retry = 0; retry < 10; retry++) {
      const startR = rMin + Math.floor(rng() * rSpan);
      const startCol = colMin + Math.floor(rng() * colSpan);
      sq = startCol - ((startR + (startR & 1)) >> 1); sr = startR;
      const endR = rMin + Math.floor(rng() * rSpan);
      const endCol = colMin + Math.floor(rng() * colSpan);
      eq = endCol - ((endR + (endR & 1)) >> 1); er = endR;
      if (hexDistance(sq, sr, eq, er) >= MIN_ROUTE_DIST) break;
    }
    requests.push(buildPathfindingRequest(
      { q: sq, r: sr }, { q: eq, r: er },
      TravelMode.Normal, travelConfig, worldInfo, sunKeys,
    ));
  }

  const workers: Worker[] = [];
  for (let i = 0; i < WORKER_COUNT; i++) {
    workers.push(new Worker(
      new URL('../workers/pathfinding.worker.ts', import.meta.url),
      { type: 'module' },
    ));
  }

  const routes: StressRoute[] = [];
  const reqsPerWorker: PathfindingRequest[][] = Array.from({ length: WORKER_COUNT }, () => []);
  for (let i = 0; i < requests.length; i++) reqsPerWorker[i % WORKER_COUNT].push(requests[i]);

  const chains: Promise<void>[] = [];
  for (let wi = 0; wi < WORKER_COUNT; wi++) {
    const wkr = workers[wi];
    const reqs = reqsPerWorker[wi];
    let chain = Promise.resolve();
    for (let ri = 0; ri < reqs.length; ri++) {
      chain = chain.then(() => pathfindOne(wkr, reqs[ri])).then((resp) => {
        const sr = createStressRoute(resp, travelConfig, hexRadius, gl, fleetConfig, pipeline, showLines);
        if (sr) routes.push(sr);
      });
    }
    chains.push(chain);
  }
  await Promise.all(chains);
  for (const wkr of workers) wkr.terminate();

  if (routes.length === 0) {
    const midR = Math.floor(h / 2);
    const off = (midR + (midR & 1)) >> 1;
    const midCol = Math.floor(w / 2);
    const req = buildPathfindingRequest(
      { q: midCol - 30 - off, r: midR }, { q: midCol + 30 - off, r: midR },
      TravelMode.Normal, travelConfig, worldInfo, sunKeys,
    );
    const worker = new Worker(
      new URL('../workers/pathfinding.worker.ts', import.meta.url),
      { type: 'module' },
    );
    const resp = await pathfindOne(worker, req);
    worker.terminate();
    const sr = createStressRoute(resp, travelConfig, hexRadius, gl, fleetConfig, pipeline, showLines);
    if (sr) routes.push(sr);
  }

  return routes;
}
